import React, { useState, useEffect } from 'react';
import { supermercadoService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function SupermercadoManagement() {
  const [supermercados, setSupermercados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSupermercado, setEditingSupermercado] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    direccion: '',
    telefono: '',
    activo: true
  });

  useEffect(() => {
    fetchSupermercados();
  }, []);

  const fetchSupermercados = async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      const response = await supermercadoService.getAllSupermercados();
      if (response.success) {
        setSupermercados(response.data || []);
      } else {
        setError(response.message || 'Error al cargar los supermercados');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar los supermercados');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTrace(null);
    setSuccess('');

    try {
      let response;
      if (editingSupermercado) {
        response = await supermercadoService.updateSupermercado(editingSupermercado.id, formData);
      } else {
        response = await supermercadoService.createSupermercado(formData);
      }

      if (response.success) {
        setSuccess(editingSupermercado ? 'Supermercado actualizado exitosamente' : 'Supermercado creado exitosamente');
        resetForm();
        fetchSupermercados();
      } else {
        setError(response.message || 'Error al guardar el supermercado');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al guardar el supermercado');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const handleEdit = (supermercado) => {
    setEditingSupermercado(supermercado);
    setFormData({
      nombre: supermercado.nombre,
      codigo: supermercado.codigo || '',
      direccion: supermercado.direccion || '',
      telefono: supermercado.telefono || '',
      activo: supermercado.activo
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este supermercado?')) {
      try {
        setError('');
        setErrorTrace(null);
        const response = await supermercadoService.deleteSupermercado(id);
        if (response.success) {
          setSuccess('Supermercado eliminado exitosamente');
          fetchSupermercados();
        } else {
          setError(response.message || 'Error al eliminar el supermercado');
          setErrorTrace(response.errorTrace || null);
        }
      } catch (err) {
        const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al eliminar el supermercado');
        setError(message);
        setErrorTrace(trace);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      codigo: '',
      direccion: '',
      telefono: '',
      activo: true
    });
    setEditingSupermercado(null);
    setShowForm(false);
  };

  const openNewSupermercadoForm = () => {
    resetForm();
    setShowForm(true);
  };

  return (
    <div className="supermercado-management">
      <div className="section-header">
        <h2>Gestión de Supermercados</h2>
        <div className="header-actions">
          <button onClick={openNewSupermercadoForm} className="btn btn-primary">
            + Nuevo Supermercado
          </button>
        </div>
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />
      {success && <p className="success-message">{success}</p>}

      {showForm && (
        <div className="supermercado-form-container">
          <form onSubmit={handleSubmit} className="supermercado-form">
            <h3>{editingSupermercado ? 'Editar Supermercado' : 'Nuevo Supermercado'}</h3>
            
            <div className="input-group">
              <label htmlFor="nombre">Nombre</label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Nombre del supermercado"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="codigo">Código</label>
              <textarea
                id="codigo"
                name="codigo"
                value={formData.codigo}
                onChange={handleChange}
                placeholder="Código del supermercado (hasta 3000 caracteres)"
                maxLength="3000"
                rows="4"
              />
              <small className="input-hint">
                {formData.codigo ? formData.codigo.length : 0} / 3000 caracteres
              </small>
            </div>

            <div className="input-group">
              <label htmlFor="direccion">Dirección</label>
              <input
                type="text"
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Dirección"
              />
            </div>

            <div className="input-group">
              <label htmlFor="telefono">Teléfono</label>
              <input
                type="text"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Teléfono"
              />
            </div>

            {editingSupermercado && (
              <div className="input-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleChange}
                  />
                  Supermercado activo
                </label>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingSupermercado ? 'Actualizar' : 'Crear'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Cargando supermercados...</p>
      ) : supermercados.length === 0 ? (
        <div className="empty-state">
          <p>No hay supermercados registrados</p>
        </div>
      ) : (
        <div className="supermercados-table">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Código</th>
                <th>Dirección</th>
                <th>Teléfono</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {supermercados.map((supermercado) => (
                <tr key={supermercado.id} className={!supermercado.activo ? 'inactive-row' : ''}>
                  <td>{supermercado.nombre}</td>
                  <td>{supermercado.codigo || '-'}</td>
                  <td>{supermercado.direccion || '-'}</td>
                  <td>{supermercado.telefono || '-'}</td>
                  <td>
                    <span className={`status-badge ${supermercado.activo ? 'status-active' : 'status-inactive'}`}>
                      {supermercado.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleEdit(supermercado)}
                      className="btn btn-small"
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDelete(supermercado.id)}
                      className="btn btn-small btn-danger"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SupermercadoManagement;
